import config from '../../../config';
import {IBlock, Integration} from '../../models';
import {getIconFor} from '../../utils/custom-icons';
import {BaseBlock} from './types';
import {BLOCK_TYPE, EXECUTION_STATUS} from './constants';

/*
 * TODO - move utils into a folder and group util functions by type
 */

export const createBaseBlock = (type: string, name: string = '') => {
  /*
   * create simple block object
   */
  return {
    type,
    id: (new Date()).getTime(),
    name: name, // set in the reducer based on type
  }
}

export const convertToBlockEntity = (block: BaseBlock): Partial<IBlock> => {
  const {
    type,
    name,
    verbose_name,
    position,
    is_dirty,
    ...rest
  } = block;
  return {
    type,
    name,
    verbose_name: verbose_name || name,
    content_json: JSON.stringify(rest),
  };
};

const customIconProps = {
  width: 16,
  height: 16,
};

export const integrationToSelectOption = (value: Integration) => ({
  label: value.name,
  value: value.uid as string,
  icon: getIconFor(value.type as string, customIconProps),
});

export const shouldLoadResult = (resultId: string | undefined | null, result: any): boolean => {
  /*
   * Compare loaded `result` against a `resultId` identifier.
   * Used in case `result_id` is provided for a Block.
   *
   * In case `resultId` is provided and `result` exists, compare against `result` identifier in order to determine
   *   if we should load the result or not.
   *
   */
  const FINISH_STATUS = [EXECUTION_STATUS.SUCCESS, EXECUTION_STATUS.FAILED];

  if (resultId && !!result) {
    const {key, task_run_id, status} = result;

    if ((resultId === key || resultId === task_run_id) && FINISH_STATUS.includes(status)) {
      /*
       * If `resultId` is the same with `result` identifier and `result` status is finished,
       *   we should not load result.
       */
      return false;
    }
  }

  return true;
}

export const fromBlockToContent = (block: IBlock): BaseBlock => {
  const {
    id: block_id,
    uid,
    type,
    content_json,
    name,
    verbose_name,
    position,
    ...rest
  } = block;

  let contentJson = content_json;
  if (typeof contentJson === 'string') {
    contentJson = JSON.parse(content_json);
  }

  return {
    ...contentJson,
    id: contentJson.id || block_id,
    uid,
    type,
    block_id,
    name,
    verbose_name,
  } as BaseBlock;
}

export const fromBlocksToContent = (blocks: IBlock[]): BaseBlock[] => {
  if (!blocks) {
    return [];
  }

  return blocks.map(fromBlockToContent);
}

export const toggleAceReadOnly = (editor: any, value: string | null, readOnly: boolean) => {
  /*
   * Toggle `AceEditor` readOnly mode (used in preview mode)
   * @TODO - rename to make it specific?
   */
  if (readOnly) {
    editor.setReadOnly(true);
    editor.setOption('wrap', true);

    editor.renderer.canvas.parentElement.style.background = '#f9f9f9';
    editor.session._tmpValue = editor.session.getValue(); // set `_tmpValue`
    editor.session.setValue(value || '');
  } else {
    editor.renderer.canvas.parentElement.style.background = '';
    editor.session.setValue(editor.session._tmpValue || '');
    editor.session._tmpValue = undefined;
    /*
     * Set readonly to `false` after setting the new value, so it does not trigger `onChange`
     */
    editor.setReadOnly(false);
    editor.setOption('wrap', false);
  }
};

export const aceEditorReadOnly = (editor: any): boolean => {
    if (!editor) {
      return true;
    }
    return editor.getReadOnly();
}

export const toggleTinyReadOnly = (editor: any, value: string | null, readOnly: boolean) => {
  /*
   * Toggle TinyMCE editor readOnly mode
   */
  if (readOnly) {
    editor.mode.set('readonly');
    editor._tmpContent = editor.getContent(); // set `_tmpContent`
    editor.setContent(value);
  } else {
    editor.setContent(editor._tmpContent || '');
    editor._tmpContent = undefined;
    /*
     * Set mode to `design` after setting the new value, so it does not trigger `onChange`
     */
    editor.mode.set('design');
  }
};

export const tinyEditorReadOnly = (editor: any): boolean => {
  if (!editor) {
    return false;
  }
  return editor.mode.isReadOnly();
}

export const getBlockTypeOutput = (type: BLOCK_TYPE): string[] => {
  switch (type) {
    case BLOCK_TYPE.API_REQUEST:
    case BLOCK_TYPE.CUSTOM_APP:
    case BLOCK_TYPE.OPEN_AI:
      return ['data'];
    case BLOCK_TYPE.SQL:
      return ['data', 'export()'];
    case BLOCK_TYPE.EXTERNAL_FILE:
    case BLOCK_TYPE.TABLEAU:
      return ['export()'];
    default:
      return [];
  }
};

export const getValidName = (verboseName: string): string | null => {
  /*
   * name must be between 1 and 64 characters
   * name must not start with a number
   * name is allowed to contain only \w characters [a-zA-Z0-9_]
   */
  /* replace space with _ */
  let name: string = verboseName.replaceAll(/ /g, '_'); // replace space with _
  /* replace everything that is not [a-zA-Z0-9_] with '' */
  name = name.replaceAll(/\W/gi, '');

  if (name.length > 0 && name.length <= 64 && !!name.match(/^[_a-zA-Z]/)) {
    return name;
  }
  return null;
}

export const getDownloadUrl = (uid: string, key: string, type: string, exportFile?: string): string => {
  /*
   * - `uid` is the block's uid
   * - `key` is `resultId`
   * - `type` is CSV, XLSX, etc.
   * - `exportFile` is used for "destination" type blocks, like email, slack, etc.
   */
  let url = config.app.url + '/api/v1/block/' + uid + '/export/' + key + '?type=' + type;

  if (exportFile) {
    url += '&export_file=' + exportFile;
  }
  return url;
};

export function updateDotNotationObject(obj: any, key: string, value: any) {
  /*
   * Update a dot notation object
   * https://stackoverflow.com/questions/6393943/convert-javascript-string-in-dot-notation-into-an-object-reference
   */
  const keys = key.split('.');
  const lastKey = keys.pop();
  const lastObj = keys.reduce((acc, k) => {
    if (!acc[k]) {
      acc[k] = {};
    }
    return acc[k];
  }, obj);
  lastObj[lastKey as string] = value;
  return obj;
}
